// @file Network requests related to custom property responses from surface
// We use this API for voting and ratings custom properties on posts

import { asciiSafeStringify } from '@@/bits/json_stringify'
import { fetchJson } from '@@/surface/api_fetch'
import type { CustomPropertyResponse, HashId, WallCustomPostProperty } from '@@/types'
import type { JsonAPIResponse } from '@padlet/arvo'
import { HTTPMethod } from '@padlet/fetch'

const API_VERSION = 1

interface UpsertCustomPropertyResponsePayload {
  wishHashId: HashId
  customPropertyId: NonNullable<WallCustomPostProperty['id']>
  value: number
}

interface DeleteCustomPropertyResponsePayload {
  wishHashId: HashId
  customPropertyId: NonNullable<WallCustomPostProperty['id']>
}

class CustomPropertyResponseApi {
  /**
   * Upsert a custom property response (create or update)
   * @param payload - Contains wishHashId, customPropertyId, and value
   * @returns The created/updated custom property response
   */
  public static async upsert(
    payload: UpsertCustomPropertyResponsePayload,
  ): Promise<JsonAPIResponse<CustomPropertyResponse>> {
    const { wishHashId, customPropertyId, value } = payload

    return await fetchJson(`/api/${API_VERSION}/wishes/${wishHashId}/custom-property-responses/${customPropertyId}`, {
      method: HTTPMethod.put,
      body: asciiSafeStringify({ value }),
    })
  }

  /**
   * Delete a custom property response
   * @param payload - Contains wishHashId and customPropertyId
   * @returns The deleted custom property response
   */
  public static async delete(
    payload: DeleteCustomPropertyResponsePayload,
  ): Promise<JsonAPIResponse<CustomPropertyResponse>> {
    const { wishHashId, customPropertyId } = payload

    return await fetchJson(`/api/${API_VERSION}/wishes/${wishHashId}/custom-property-responses/${customPropertyId}`, {
      method: HTTPMethod.delete,
    })
  }
}

export { CustomPropertyResponseApi }
export type { UpsertCustomPropertyResponsePayload, DeleteCustomPropertyResponsePayload }
